import { useOrganisation } from '@/app/redux/hooks/organisation';
import { BillingInfo } from '@/common/types/organisation';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import stripePromise from '@/common/utils/stripe';
import api from '@/db/apiClient';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { AddressElement, Elements } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { toast } from 'sonner';

interface UpdateBillingAddressDialogueProps {
  isOpen: boolean;
  onClose: () => void;
}
export default function UpdateBillingAddressDialogue({
  isOpen,
  onClose,
}: UpdateBillingAddressDialogueProps) {
  const { organisation } = useOrganisation();
  const license = organisation?.license;
  const stripeCustomerId = license?.stripeCustomerId;
  const existingBillingInfo = license?.billingInfo;

  const [loading, setLoading] = useState(false);
  const [billingInfoFilled, setBillingInfoFilled] = useState(false);
  const [updatedBillingInfo, setUpdatedBillingInfo] =
    useState<BillingInfo | null>(existingBillingInfo ?? null);

  async function handleUpdateBillingInfo() {
    if (!stripeCustomerId) {
      console.error('Stripe customer ID is missing');
      toast.error('Failed to update billing address', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    if (!updatedBillingInfo) {
      toast.error('Failed to update billing address', {
        description: 'Please fill in all the fields',
      });
      return;
    }
    try {
      setLoading(true);
      await api.asEducator.updateBillingAddress({
        customerId: stripeCustomerId,
        billingInfo: updatedBillingInfo,
      });
      trackMixpanelEvent('Updated Billing Address', {
        source: 'manage_subscription_dialog',
        currentPlan: license.plan,
      });
      toast.success('Billing address updated');
      onClose();
    } catch (error) {
      console.error('Error updating billing address:', error);
      toast.error('Failed to update billing address', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setBillingInfoFilled(false);
        !open && onClose();
      }}
    >
      <DialogContent className="text-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle>Update Billing Address</DialogTitle>
          <DialogDescription>
            Update the billing address for your subscription.
          </DialogDescription>
        </DialogHeader>

        <Elements stripe={stripePromise}>
          <div className="border border-border rounded-md p-4">
            <AddressElement
              options={{
                mode: 'billing',
                fields: {
                  phone: 'always',
                },
                defaultValues: {
                  address: {
                    country: existingBillingInfo?.address?.country ?? 'NZ',
                    line1: existingBillingInfo?.address?.line1 ?? '',
                    line2: existingBillingInfo?.address?.line2 ?? '',
                    city: existingBillingInfo?.address?.city ?? '',
                    state: existingBillingInfo?.address?.state ?? '',
                    postal_code:
                      existingBillingInfo?.address?.postal_code ?? '',
                  },
                  name: existingBillingInfo?.name,
                  phone: existingBillingInfo?.phone,
                },
              }}
              onChange={(e) => {
                setBillingInfoFilled(e.complete);
                setUpdatedBillingInfo({
                  address: e.value.address,
                  name: e.value.name ?? '',
                  phone: e.value.phone ?? null,
                  email: existingBillingInfo?.email ?? '',
                  card: existingBillingInfo?.card ?? null,
                });
              }}
            />
          </div>
        </Elements>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={loading}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={!billingInfoFilled}
            loading={loading}
            onClick={handleUpdateBillingInfo}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
